/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiInformationCircle } from 'react-icons/hi' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import BlockIpDropDownOptions from 'components/BlockIpDropDownOptions'
import BlockIpUpsert from 'components/BlockIpUpsert'
import BlockIpDelete from 'components/BlockIpDelete'


/*
 * GRAPHS
 */
import BlockIpReadQuery from './__query__/index.blockIp.read.query'


/*
 * STYLES
 */
import { buttonStyle } from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ 'items': [] })
  const [sortModel, setSortModel] = React.useState([])
  const [blockIpDisplayNameToSearch, setBlockIpDisplayNameToSearch] = React.useState('')

  const { 'isOpen': isBlockIpCreateOpen, 'onOpen': onBlockIpCreateOpen, 'onClose': onBlockIpCreateClose } = useDisclosure()
  const { 'isOpen': isBlockIpUpdateOpen, 'onOpen': onBlockIpUpdateOpen, 'onClose': onBlockIpUpdateClose } = useDisclosure()
  const { 'isOpen': isBlockIpDeleteOpen, 'onOpen': onBlockIpDeleteOpen, 'onClose': onBlockIpDeleteClose } = useDisclosure()

  const _onSearchInputChange = React.useRef(_.debounce((value) => setBlockIpDisplayNameToSearch(value), 800)).current

  // Extract search filter and trigger debounced update
  React.useEffect(() => {
    const nameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = nameFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [filterModel])

  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  const _QueryBlockIpRead = useQuery(BlockIpReadQuery, {
    'variables': {
      'blockIpDirectoryId': passOn?.blockIpDirectoryId,
      blockIpDisplayNameToSearch,
      'skip': paginationModel.page * paginationModel.pageSize,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Data Assignment.
  if (!_QueryBlockIpRead.loading && 0 < _QueryBlockIpRead.data?.BlockIpRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryBlockIpRead?.data?.BlockIpRead?.length || (0 < _QueryBlockIpRead?.data?.BlockIpRead?.length && !_.every(_.pluck(_QueryBlockIpRead.data?.BlockIpRead, 'status'), i => _successFlags.includes(i)))) _isFirstLoadCompleted.current = false

  // Define columns
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      'headerAlign': 'center'
    },
    {
      'field': 'displayName',
      'headerName': 'Display Name',
      'description': 'Name of the blocked IP.',
      'editable': false,
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last update date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Available actions.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      renderCell: ({ row }) => (
        <BlockIpDropDownOptions
          blockIpId={row?.id}
          blockIpDirectoryId={passOn?.blockIpDirectoryId}
          onBlockIpUpdate={onBlockIpUpdateOpen}
          onBlockIpDelete={onBlockIpDeleteOpen}
        />
      )
    }
  ], [passOn?.blockIpDirectoryId])

  // Define rows
  const rows = React.useMemo(() => _.compact(_QueryBlockIpRead?.data?.BlockIpRead?.map((item, index) => _successFlags.includes(item.status) ? ({
    ...item,
    'id': item?.id || `blockip-${index}-${Date.now()}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': item?.displayName ?? '-',
    'createdAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryBlockIpRead?.data?.BlockIpRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='blockIp base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          position='relative'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          maxH='81vh'
          minH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex
            pb='0px'
            justify='space-between'
            flexDir='row'
            gap='12px'>
            <Flex gap='12px' flexDir={{ 'base': 'column', 'md': 'row' }}>
              <Tooltip label='Create New BlockIp' fontSize='sm'>
                <Button
                  w='170px'
                  leftIcon={<IoMdAdd />}
                  onClick={onBlockIpCreateOpen}
                  style={buttonStyle}>
                  Create Block Ip
                </Button>
              </Tooltip>
              {
                _isCurrentViewMobile ? void 0 : (
                  <Button
                    leftIcon={<HiInformationCircle />}
                    style={_.omit(buttonStyle, 'backgroundColor', 'color')}
                    _hover={{ 'bg': 'red.50' }}
                    color='red'
                    bg='red.50'
                    h={{ 'base': 'fit-content', 'md': 'inherit' }}>
                    <Text
                      whiteSpace='normal'
                      width='100%'
                      textAlign='start'
                      display='flex'
                      flexDirection='column'>
                      All IP&apos;s Folder listed are blocked on Network Level of OSI.
                    </Text>
                  </Button>
                )
              }
            </Flex>
            <Flex>
              <DownloadToExcel
                cellsData={
                  0 === _QueryBlockIpRead?.data?.BlockIpRead?.length || !_.every(_.pluck(_QueryBlockIpRead?.data?.BlockIpRead, 'status'), i => _successFlags.includes(i)) ? [] : _QueryBlockIpRead?.data?.BlockIpRead.map(
                    (item, __index) => ({
                      'S.No.': `${__index + (paginationModel.pageSize * paginationModel.page) + 1}.`,
                      'Display Name': item.displayName ?? '-',
                      'Created At': Moment(item?.createdAt).format('YYYY-MM-DD'),
                      'Updated At': Moment(item?.updatedAt).format('YYYY-MM-DD')
                    })
                  )
                }
                headersData={['S.No.', 'Display Name', 'Created At', 'Updated At'].map(i => ({ 'key': i, 'label': i }))}
              />
            </Flex>
          </Flex>

          {/* MUI DataGrid */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryBlockIpRead?.data?.BlockIpRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            filterMode='client'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            sortingMode='client'
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[8, 20, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryBlockIpRead?.loading}
            disableColumnMenu={false}
          />
        </Flex>
        <Modal
          size='sm'
          title='Block Ip Create'
          isOpen={isBlockIpCreateOpen}
          onClose={onBlockIpCreateClose}
        >
          <BlockIpUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='sm'
          title='Block Ip Update'
          isOpen={isBlockIpUpdateOpen}
          onClose={onBlockIpUpdateClose}
        >
          <BlockIpUpsert />
        </Modal>
        <Modal
          size='sm'
          title='Block Ip Delete'
          isOpen={isBlockIpDeleteOpen}
          onClose={onBlockIpDeleteClose}
        >
          <BlockIpDelete />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
